import React, { useState, useEffect, useRef } from 'react';
import { Brain, Check, Loader2, ChevronRight, ChevronDown, Clock, Zap } from 'lucide-react';

interface ThinkingStep {
  id: string;
  title: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  description: string;
  action: string;
  needsConfirmation?: boolean;
  result?: any;
  error?: string;
}

interface ThinkingAgentV2Props {
  steps: ThinkingStep[];
  currentThinkingContent: string;
  onContinue?: () => void;
  onPause?: () => void;
  isPaused?: boolean;
  needsConfirmation?: boolean;
  confirmationMessage?: string;
  isComplete?: boolean; // NOUVEAU: pour savoir si c'est terminé
}

/**
 * THINKING AGENT V2 - Style sobre, élégant, NOIR comme les concurrents
 * AVEC COLLAPSE/EXPAND + TYPEWRITER EFFECT
 */
const ThinkingAgentV2: React.FC<ThinkingAgentV2Props> = ({
  steps,
  currentThinkingContent,
  onContinue,
  isPaused = false,
  needsConfirmation = false,
  confirmationMessage = '',
  isComplete = false
}) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [autoAcceptCountdown, setAutoAcceptCountdown] = useState(30);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [shouldAutoCollapse, setShouldAutoCollapse] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // TYPEWRITER EFFECT - Affichage char par char
  useEffect(() => {
    if (!currentThinkingContent) {
      setDisplayedText('');
      return;
    }

    // Si le nouveau contenu est plus long, on ajoute char par char
    if (currentThinkingContent.length > displayedText.length) {
      const timer = setTimeout(() => {
        setDisplayedText(currentThinkingContent.substring(0, displayedText.length + 3)); // +3 chars à la fois pour plus de fluidité
      }, 10); // 10ms entre chaque ajout
      return () => clearTimeout(timer);
    } else {
      // Si le contenu est plus court (nouveau step), réinitialiser
      setDisplayedText(currentThinkingContent);
    }
  }, [currentThinkingContent, displayedText]);

  // AUTO-SCROLL vers le bas
  useEffect(() => {
    if (contentRef.current && !isCollapsed) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [displayedText, steps, isCollapsed]);

  // Countdown auto-accept
  useEffect(() => {
    if (needsConfirmation && autoAcceptCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoAcceptCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (needsConfirmation && autoAcceptCountdown === 0) {
      onContinue?.();
    }
  }, [needsConfirmation, autoAcceptCountdown, onContinue]);

  // Auto-expand step en cours
  useEffect(() => {
    const currentStep = steps.find(s => s.status === 'in_progress');
    if (currentStep && expandedStep !== currentStep.id) {
      setExpandedStep(currentStep.id);
    }
  }, [steps, expandedStep]);

  // Auto-collapse après step 5 (synthesize)
  useEffect(() => {
    const allStepsCompleted = steps.every(s => s.status === 'completed');
    const synthesizeStep = steps.find(s => s.id.includes('synthesize') || s.title.toLowerCase().includes('synthétiser'));
    
    if (allStepsCompleted || (synthesizeStep && synthesizeStep.status === 'completed')) {
      setShouldAutoCollapse(true);
      // Auto-collapse après 2 secondes
      setTimeout(() => {
        setIsCollapsed(true);
      }, 2000);
    }
  }, [steps]);

  const currentStepIndex = steps.findIndex(s => s.status === 'in_progress');
  const completedSteps = steps.filter(s => s.status === 'completed').length;

  // Parser le contenu de manière sobre
  const parseContent = (content: string) => {
    if (!content) return [];
    
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      
      // Titre de section (avec ***)
      if (trimmed.includes('***')) {
        return { type: 'title', content: trimmed.replace(/\*/g, ''), key: i };
      }
      // Titre markdown (##)
      if (trimmed.startsWith('##')) {
        return { type: 'heading', content: trimmed.replace(/^##\s*/, ''), key: i };
      }
      // Liste
      if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
        return { type: 'bullet', content: trimmed.substring(2), key: i };
      }
      // Texte normal
      return { type: 'text', content: trimmed, key: i };
    }).filter(Boolean);
  };

  const parsedContent = parseContent(displayedText); // Utiliser displayedText au lieu de currentThinkingContent

  // Si pas de steps, ne rien afficher (évite le scintillement)
  if (steps.length === 0) {
    return null;
  }

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: calc(200px + 100%) 0; }
        }
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
          background-size: 200px 100%;
          animation: shimmer 2s infinite;
        }
      `}</style>
      <div className="flex justify-start mb-4">
        <div className="w-full max-w-6xl">
        {/* HEADER COLLAPSE - Style EXACT des images (sobre, simple) */}
        <div className="mb-3">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="group flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">Thinking Details</span>
          </button>
        </div>

        {/* CONTENU - Layout 2 colonnes SOBRE (masqué si collapsed) */}
        {!isCollapsed && (
          <div className="flex gap-4">
            
            {/* COLONNE GAUCHE - Steps (STYLE EXACT DES IMAGES) */}
            <div className="w-48 flex-shrink-0">
              <div className="space-y-1">
                {steps.map((step, index) => {
                  const isCompleted = step.status === 'completed';
                  const isCurrent = step.status === 'in_progress';

                  return (
                    <button
                      key={step.id}
                      onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
                      className="w-full flex items-center space-x-2 px-2 py-2 text-left hover:bg-gray-50 rounded transition-colors"
                    >
                      {/* Checkmark NOIR simple comme dans les images */}
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isCompleted 
                          ? 'bg-theme-base' 
                          : isCurrent 
                          ? 'bg-gray-400' 
                          : 'bg-gray-200'
                      }`}>
                        {isCompleted ? (
                          <Check className="w-3 h-3 text-theme-primary" />
                        ) : isCurrent ? (
                          <Loader2 className="w-3 h-3 text-theme-primary animate-spin" />
                        ) : null}
                      </div>

                      {/* Titre compact */}
                      <span className={`text-sm ${
                        isCompleted || isCurrent ? 'text-gray-900 font-medium' : 'text-gray-600'
                      }`}>
                        {step.title.length > 20 ? step.title.substring(0, 17) + '...' : step.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* COLONNE DROITE - Contenu (STYLE SOBRE DES IMAGES) */}
            <div className="flex-1">
              <div className="bg-gray-50 rounded-lg border border-gray-200">
                {/* Contenu sobre sans header */}
                <div 
                  ref={contentRef}
                  className="p-4 max-h-64 overflow-y-auto space-y-2"
                  style={{ scrollBehavior: 'smooth' }}
                >
                  {parsedContent.length > 0 ? (
                    parsedContent.map((item: any) => {
                      if (item.type === 'title') {
                        return (
                          <h3 key={item.key} className="text-sm font-bold text-gray-900 mt-3 first:mt-0 mb-2">
                            {item.content}
                          </h3>
                        );
                      }
                      if (item.type === 'heading') {
                        return (
                          <h4 key={item.key} className="text-sm font-semibold text-gray-800 mt-2 first:mt-0 mb-1">
                            {item.content}
                          </h4>
                        );
                      }
                      if (item.type === 'bullet') {
                        return (
                          <div key={item.key} className="flex items-start space-x-2 ml-2">
                            <span className="text-gray-600 mt-0.5">•</span>
                            <span className="text-xs text-gray-700 leading-relaxed">{item.content}</span>
                          </div>
                        );
                      }
                      return (
                        <p key={item.key} className="text-xs text-gray-600 leading-relaxed">
                          {item.content}
                        </p>
                      );
                    })
                  ) : (
                    <div className="text-center py-6">
                      <Loader2 className="w-5 h-5 text-theme-secondary animate-spin mx-auto mb-2" />
                      <p className="text-xs text-theme-tertiary">Analyse en cours...</p>
                    </div>
                  )}

                {/* Confirmation sobre */}
                {needsConfirmation && (
                  <div className="mt-4 flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      {confirmationMessage || 'Continuer l\'analyse ?'}
                    </p>
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-theme-tertiary">Auto-adopted in {autoAcceptCountdown}s</span>
                      <button
                        onClick={onContinue}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-theme-primary text-sm font-medium rounded-lg transition-colors"
                      >
                        Go on
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  );
};

export default ThinkingAgentV2;
